// F-PPL-13 — sıra kalıcı seçim kimliğidir; mevcut avatarların yerleri değişmemeli.
// Sekiz ad, API'deki HAZIR_AVATAR_SAYISI ile HazirAvatar.test.tsx'te karşılaştırılır.
// UI paketine sunucu bağımlılığı taşımamak için bu bağ yalnızca testte kurulur.
export const HAZIR_AVATAR_ADLARI = [
  "Roket",
  "Pusula",
  "Kitap",
  "Fikir",
  "Zirve",
  "Yıldız",
  "Baykuş",
  "Gezegen",
] as const;

export function hazirAvatarAdi(secim: number): string {
  return (Number.isInteger(secim) && HAZIR_AVATAR_ADLARI[secim]) || "Nötr";
}

export interface HazirAvatarProps {
  secim: number;
  boyut?: number;
  etiket?: string;
  "aria-hidden"?: boolean;
}

export function HazirAvatar({
  secim,
  boyut = 64,
  etiket,
  "aria-hidden": gizli = false,
}: HazirAvatarProps) {
  const gecerli = Number.isInteger(secim) && secim >= 0 && secim < HAZIR_AVATAR_ADLARI.length;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={boyut}
      height={boyut}
      className="shrink-0"
      role={gizli ? undefined : "img"}
      aria-label={gizli ? undefined : (etiket ?? `${hazirAvatarAdi(secim)} avatarı`)}
      aria-hidden={gizli || undefined}
      focusable="false"
    >
      {/* Kendi opak zemini, koyu kabukta da aynı simge kontrastını korur. */}
      <circle
        cx="32"
        cy="32"
        r="32"
        fill={gecerli ? "var(--color-marka-50)" : "var(--color-notr-100)"}
      />
      <g
        fill="var(--color-notr-0)"
        stroke="var(--color-marka-700)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {gecerli ? (
          <Simge secim={secim} />
        ) : (
          <circle cx="32" cy="32" r="10" stroke="var(--color-notr-500)" />
        )}
      </g>
    </svg>
  );
}

// Kalın dış hatlar ve geniş dolgular, 32 px'te ayrımı renge bağımlı bırakmaz.
// Turuncu ayrıntılar `vurgu-700`: `vurgu-600` açık mavi dolgu (`marka-200`) üstünde 2,51:1 kalıyordu
// (Codex tur 24, WCAG 1.4.11 grafik nesne eşiği 3:1); `vurgu-700` orada ~3,65, beyazda 5,18.
function Simge({ secim }: { secim: number }) {
  switch (secim) {
    case 0:
      return (
        <>
          <path d="M23 38Q14 39 14 50Q25 50 26 41" fill="var(--color-vurgu-700)" />
          <path d="M24 25L15 29L13 39L25 35M39 40L35 49L25 51L29 39" />
          <path d="M23 34Q29 14 49 14Q49 34 30 41Z" />
          <path d="M38 16L47 25" />
          <circle cx="36" cy="27" r="5" fill="var(--color-marka-200)" />
          <path d="M13 17H19M16 14V20" stroke="var(--color-vurgu-700)" />
        </>
      );
    case 1:
      return (
        <>
          <circle cx="32" cy="32" r="21" />
          <path d="M32 11V16M53 32H48M32 53V48M11 32H16" />
          <path d="M23 41L28 28L41 23L36 36Z" fill="var(--color-marka-200)" />
          <path d="M28 28L41 23L36 36Z" fill="var(--color-vurgu-700)" />
          <circle cx="32" cy="32" r="2" fill="var(--color-marka-700)" stroke="none" />
        </>
      );
    case 2:
      return (
        <>
          <path d="M11 18Q22 14 32 21Q42 14 53 18V47Q42 43 32 50Q22 43 11 47Z" />
          <path d="M32 21V50M17 27Q22 26 26 29M17 35Q22 34 26 37M38 35Q43 32 47 33" />
          <path d="M40 17V29L44 26L48 28V17" fill="var(--color-vurgu-700)" />
        </>
      );
    case 3:
      return (
        <>
          <path d="M24 41C24 36 18 33 18 26A14 14 0 0 1 46 26C46 33 40 36 40 41Z" />
          <path d="M29 41V32L25 28M35 41V32L39 28" stroke="var(--color-vurgu-700)" />
          <path d="M24 41H40V48H24ZM28 53H36" fill="var(--color-marka-200)" />
          <path
            d="M32 5V7M10 13L14 16M50 16L54 13M7 28H11M53 28H57"
            stroke="var(--color-vurgu-700)"
          />
        </>
      );
    case 4:
      return (
        <>
          <circle cx="46" cy="19" r="6" fill="var(--color-vurgu-700)" stroke="none" />
          <path d="M9 49L23 27L37 49Z" fill="var(--color-marka-200)" />
          <path d="M20 49L36 19L54 49Z" />
          <path d="M29 32L35 34L40 28" />
          <path d="M33 49L39 42L35 39" stroke="var(--color-vurgu-700)" />
          <path d="M9 49H54" />
        </>
      );
    case 5:
      return (
        <>
          <path
            d="M32 12L38 25L52 27L42 37L44 51L32 44L20 51L22 37L12 27L26 25Z"
            fill="var(--color-vurgu-700)"
          />
          <path d="M27 29L32 20L35 27" stroke="var(--color-notr-0)" />
          <path d="M12 13H18M15 10V16M49 17H55M52 14V20" />
        </>
      );
    case 6:
      return (
        <>
          <path
            d="M15 15L25 21Q32 18 39 21L49 15V36Q49 51 32 52Q15 51 15 36Z"
            fill="var(--color-marka-200)"
          />
          <path d="M15 34Q21 36 22 44M49 34Q43 36 42 44" />
          <circle cx="25" cy="30" r="8" />
          <circle cx="39" cy="30" r="8" />
          <path
            d="M28 39L32 44L36 39"
            fill="var(--color-vurgu-700)"
            stroke="var(--color-vurgu-700)"
          />
          <path d="M26 52V55M38 52V55" stroke="var(--color-vurgu-700)" />
          <g fill="var(--color-marka-700)" stroke="none">
            <circle cx="26" cy="30" r="3" />
            <circle cx="38" cy="30" r="3" />
          </g>
        </>
      );
    case 7:
      return (
        <>
          <circle cx="32" cy="32" r="17" fill="var(--color-marka-200)" />
          <path d="M22 25Q28 20 34 23M32 41L39 38" />
          <path
            d="M16 30C-2 45 12 53 35 40C59 27 62 14 46 21"
            fill="none"
            stroke="var(--color-vurgu-700)"
            strokeWidth="5"
          />
          <path d="M47 10H53M50 7V13" />
          <circle cx="13" cy="18" r="2" fill="var(--color-vurgu-700)" stroke="none" />
        </>
      );
  }
}
